/**
 * Sprint TC-C.1 — Candidate side-by-side comparison sheet.
 *
 * Opened from `AdminRecruitmentDetail.jsx` when the HM admin / HM advisor
 * selects 2-4 candidates in the roster. Pure renderer — fetches the enriched
 * comparison payload from `recruitmentAdminApi.compare(pid, aids)` and lays
 * the candidates side-by-side as columns with attribute rows.
 *
 * Visibility:
 *  • Mount point is admin-only (the roster checkbox column only renders in
 *    `variant="admin"`).
 *  • Backend route is advisor-only — client_admin / client_hr / evaluated
 *    cannot reach this surface.
 *  • Internal scores + decision history surface here.
 */
import i18n from '@/i18n';
import { bizDate } from '@/lib/dates';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Loader2, Users, FileText, ExternalLink, TrendingUp, TrendingDown,
  ShieldAlert, AlertCircle, BadgeCheck,
} from 'lucide-react';
import { recruitmentAdminApi } from '@/lib/recruitmentApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return bizDate(iso, i18n.language); } catch { return iso; }
};

const DECISION_TONE = {
  shortlisted: 'bg-blue-100 text-blue-700 border-blue-200',
  continue_process: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  on_hold: 'bg-amber-100 text-amber-700 border-amber-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  hired: 'bg-green-100 text-green-700 border-green-200',
};

const STATUS_TONE = {
  released_to_client: 'bg-success/10 text-success border-success/20',
  approved:           'bg-info/10 text-info border-info/20',
  under_review:       'bg-blue-100 text-blue-700 border-blue-200',
  edited_by_hm:       'bg-blue-100 text-blue-700 border-blue-200',
  draft_ai_generated: 'bg-amber-100 text-amber-700 border-amber-200',
  rejected:           'bg-red-100 text-red-700 border-red-200',
};

const VERDICT_TONE = {
  suitable:                  'bg-success/10 text-success border-success/20',
  suitable_with_reservations:'bg-amber-100 text-amber-700 border-amber-200',
  not_suitable:              'bg-red-100 text-red-700 border-red-200',
};

function MetricCell({ value, band, tone, tabular = true, monoBand = false }) {
  if (value == null && !band) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {value != null && (
        <span className={`text-base font-semibold ${tabular ? 'tabular-nums' : ''}`}>
          {value}
        </span>
      )}
      {band && (
        <Badge
          variant="outline"
          className={`text-xs ${monoBand ? '' : 'capitalize'} ${tone || ''}`}
        >
          {band}
        </Badge>
      )}
    </span>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-3 border-b border-border/60">
      <div className="col-span-3 lg:col-span-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold pt-1">
        {label}
      </div>
      <div className="col-span-9 lg:col-span-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {children}
      </div>
    </div>
  );
}

function CompetencyChips({ items, tone }) {
  if (!items || items.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <ul className="space-y-1">
      {items.map((c, i) => (
        <li
          key={`${c.code || c.name || i}-${i}`}
          className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md border ${tone}`}
        >
          <span className="text-xs font-medium truncate" title={c.name}>{c.name}</span>
          <span className="text-xs tabular-nums font-semibold shrink-0">
            {c.score != null ? c.score : '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function CandidateComparisonSheet({
  open, onOpenChange, processId, selectedAids,
}) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !processId || !selectedAids?.length) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const d = await recruitmentAdminApi.compare(processId, selectedAids);
        if (alive) setData(d);
      } catch (e) {
        toast.error(
          isES ? 'No se pudo cargar la comparación' : 'Could not load comparison',
          { description: formatApiError(e) },
        );
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, processId, selectedAids, isES]);

  const candidates = useMemo(() => data?.candidates || [], [data]);
  const colsClass = useMemo(() => {
    const n = candidates.length || 1;
    if (n === 2) return 'grid-cols-1 md:grid-cols-2';
    if (n === 3) return 'grid-cols-1 md:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4';
  }, [candidates.length]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!max-w-[1280px] w-[95vw] sm:w-[90vw] overflow-y-auto"
        data-testid="comparison-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {isES ? 'Comparar candidatos' : 'Compare candidates'}
          </SheetTitle>
          <SheetDescription>
            {data?.process ? (
              <span>
                {data.process.position_title}
                {data.process.department ? ` · ${data.process.department}` : ''}
                {data.process.company_name ? ` — ${data.process.company_name}` : ''}
              </span>
            ) : (
              <span>
                {isES ? 'Comparativa interna — visible solo para CTH.' : 'Internal comparison — visible to CTH only.'}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="comparison-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {isES ? 'Cargando comparación…' : 'Loading comparison…'}
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <div className="mt-4 space-y-4">
            {/* Header row — candidate identity per column */}
            <div className={`grid gap-3 ${colsClass}`} data-testid="comparison-header-row">
              {candidates.map((c) => (
                <Card
                  key={c.assignment_id}
                  className="p-4 border-primary/15"
                  data-testid={`comparison-candidate-${c.assignment_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-display text-lg font-bold tracking-tight truncate" data-testid="comparison-candidate-name">
                        {c.candidate_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate" title={c.candidate_email}>
                        {c.candidate_email || ''}
                      </p>
                    </div>
                    {c.decision?.status && (
                      <Badge
                        variant="outline"
                        className={`text-xs ${DECISION_TONE[c.decision.status] || ''}`}
                        data-testid="comparison-decision-badge"
                      >
                        {t(`recruitment.candidates.decision.${c.decision.status}`, {
                          defaultValue: c.decision.status.replace(/_/g, ' '),
                        })}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Link to={`/admin/evaluations/${c.assignment_id}`}>
                      <Button size="sm" variant="outline" className="gap-1.5 h-7" data-testid="comparison-open-assignment">
                        <ExternalLink className="h-3 w-3" />
                        {isES ? 'Evaluación' : 'Assignment'}
                      </Button>
                    </Link>
                    {c.report_id && (
                      <Link to={`/admin/reports/${c.report_id}`}>
                        <Button size="sm" variant="outline" className="gap-1.5 h-7" data-testid="comparison-open-report">
                          <FileText className="h-3 w-3" />
                          {isES ? 'Informe' : 'Report'}
                        </Button>
                      </Link>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Attribute rows */}
            <Card className="p-5 shadow-card" data-testid="comparison-matrix">
              {/* Assessment status */}
              <Row label={isES ? 'Estado evaluación' : 'Assessment'}>
                {candidates.map((c) => (
                  <div key={`as-${c.assignment_id}`} data-testid={`comparison-cell-status-${c.assignment_id}`}>
                    <Badge variant="outline" className="text-xs">
                      {t(`recruitment.candidates.assessmentStatus.${c.assessment_status}`, {
                        defaultValue: c.assessment_status,
                      })}
                    </Badge>
                    {(c.assessment_progress || 0) > 0 && c.assessment_status !== 'submitted' && (
                      <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                        {c.assessment_progress}%
                      </span>
                    )}
                  </div>
                ))}
              </Row>

              {/* Completion */}
              <Row label={isES ? 'Completado' : 'Completion'}>
                {candidates.map((c) => (
                  <div key={`comp-${c.assignment_id}`} className="text-xs" data-testid={`comparison-cell-completion-${c.assignment_id}`}>
                    {c.assessment_status === 'submitted'
                      ? (isES ? `Enviado · ${fmtDate(c.submitted_at)}` : `Submitted · ${fmtDate(c.submitted_at)}`)
                      : (isES ? 'Pendiente' : 'Pending')}
                  </div>
                ))}
              </Row>

              {/* Report status */}
              <Row label={isES ? 'Informe' : 'Report'}>
                {candidates.map((c) => (
                  <div key={`r-${c.assignment_id}`} data-testid={`comparison-cell-report-${c.assignment_id}`}>
                    {!c.report_status || c.report_status === 'none' ? (
                      <span className="text-xs text-muted-foreground">{isES ? 'Sin informe' : 'No report'}</span>
                    ) : (
                      <Badge variant="outline" className={`text-xs ${STATUS_TONE[c.report_status] || ''}`}>
                        {t(`recruitment.candidates.reportStatus.${c.report_status}`, { defaultValue: c.report_status })}
                      </Badge>
                    )}
                    {c.report_released_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {isES ? 'Entregado' : 'Released'}: {fmtDate(c.report_released_at)}
                      </p>
                    )}
                  </div>
                ))}
              </Row>

              {/* Overall score + band */}
              <Row label={isES ? 'Puntaje global' : 'Overall score'}>
                {candidates.map((c) => (
                  <div key={`s-${c.assignment_id}`} data-testid={`comparison-cell-overall-${c.assignment_id}`}>
                    <MetricCell value={c.overall_score} band={c.score_band ? t(`reports.bands.${String(c.score_band).toLowerCase()}`, { defaultValue: c.score_band }) : c.score_band} />
                  </div>
                ))}
              </Row>

              {/* Fit score + band + verdict */}
              <Row label={isES ? 'Fit (puesto)' : 'Fit (role)'}>
                {candidates.map((c) => (
                  <div key={`f-${c.assignment_id}`} data-testid={`comparison-cell-fit-${c.assignment_id}`}>
                    <MetricCell value={c.fit_score} band={c.fit_band ? t(`reports.bands.${String(c.fit_band).toLowerCase()}`, { defaultValue: c.fit_band }) : c.fit_band} />
                    {c.fit_verdict && (
                      <p className="mt-1">
                        <Badge variant="outline" className={`text-xs ${VERDICT_TONE[c.fit_verdict] || ''}`}>
                          <BadgeCheck className="h-3 w-3 mr-1" />
                          {t(`recruitment.candidates.fitVerdict.${c.fit_verdict}`, {
                            defaultValue: c.fit_verdict.replace(/_/g, ' '),
                          })}
                        </Badge>
                      </p>
                    )}
                  </div>
                ))}
              </Row>

              {/* Top competencies */}
              <Row label={
                <span className="inline-flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-success" />
                  {isES ? 'Fortalezas' : 'Strengths'}
                </span>
              }>
                {candidates.map((c) => (
                  <div key={`top-${c.assignment_id}`} data-testid={`comparison-cell-top-${c.assignment_id}`}>
                    <CompetencyChips
                      items={c.competencies_top}
                      tone="bg-success/5 border-success/20"
                    />
                  </div>
                ))}
              </Row>

              {/* Bottom competencies */}
              <Row label={
                <span className="inline-flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-warning" />
                  {isES ? 'Áreas a desarrollar' : 'Areas to develop'}
                </span>
              }>
                {candidates.map((c) => (
                  <div key={`bot-${c.assignment_id}`} data-testid={`comparison-cell-bottom-${c.assignment_id}`}>
                    <CompetencyChips
                      items={c.competencies_bottom}
                      tone="bg-warning/5 border-warning/20"
                    />
                  </div>
                ))}
              </Row>

              {/* Critical fit gaps */}
              <Row label={
                <span className="inline-flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3 text-destructive" />
                  {isES ? 'Brechas clave' : 'Key gaps'}
                </span>
              }>
                {candidates.map((c) => (
                  <div key={`g-${c.assignment_id}`} data-testid={`comparison-cell-gaps-${c.assignment_id}`}>
                    {c.fit_gaps_top?.length ? (
                      <ul className="space-y-1">
                        {c.fit_gaps_top.map((g, i) => (
                          <li
                            key={`${g.competency_code}-${i}`}
                            className="flex items-center justify-between gap-2 px-2 py-1 rounded-md border border-destructive/20 bg-destructive/5"
                          >
                            <span className="text-xs font-medium truncate" title={g.competency_name}>
                              {g.competency_name}
                            </span>
                            <span className="text-xs tabular-nums font-semibold shrink-0">
                              {g.actual}/{g.expected}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                ))}
              </Row>

              {/* Decision + note */}
              <Row label={isES ? 'Decisión' : 'Decision'}>
                {candidates.map((c) => (
                  <div key={`dec-${c.assignment_id}`} data-testid={`comparison-cell-decision-${c.assignment_id}`}>
                    {c.decision?.status ? (
                      <>
                        <Badge variant="outline" className={`text-xs ${DECISION_TONE[c.decision.status] || ''}`}>
                          {t(`recruitment.candidates.decision.${c.decision.status}`, {
                            defaultValue: c.decision.status.replace(/_/g, ' '),
                          })}
                        </Badge>
                        {c.decision.note && (
                          <p
                            className="mt-1 text-xs italic text-muted-foreground line-clamp-3"
                            title={c.decision.note}
                            data-testid={`comparison-cell-decision-note-${c.assignment_id}`}
                          >
                            “{c.decision.note}”
                          </p>
                        )}
                        {c.decision.set_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {c.decision.set_by_name || c.decision.set_by_role} · {fmtDate(c.decision.set_at)}
                          </p>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {isES ? 'No establecida' : 'Not set'}
                      </span>
                    )}
                  </div>
                ))}
              </Row>

              {/* Last activity */}
              <Row label={isES ? 'Última actividad' : 'Last activity'}>
                {candidates.map((c) => (
                  <div key={`la-${c.assignment_id}`} className="text-xs text-muted-foreground" data-testid={`comparison-cell-last-${c.assignment_id}`}>
                    {fmtDate(c.last_activity_at)}
                  </div>
                ))}
              </Row>
            </Card>

            {!candidates.some((c) => c.overall_score != null) && (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs"
                data-testid="comparison-no-scores-hint"
              >
                <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
                <span className="text-amber-900">
                  {isES
                    ? 'Aún no hay puntajes para estos candidatos. La comparación se enriquecerá automáticamente cuando los informes estén generados.'
                    : 'No scores yet for these candidates. The comparison will auto-enrich once reports are generated.'}
                </span>
              </div>
            )}

            <Separator />
            <p className="text-xs text-muted-foreground text-center">
              {data?.compared_at
                ? (isES ? 'Generado' : 'Generated') + ': ' + fmtDate(data.compared_at)
                : ''}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
